import {Component, OnInit} from '@angular/core';
import {ImageCroppedEvent} from 'ngx-image-cropper';
import {ApiService} from '../../core/http/api.service';
import {UtilsService} from '../../core/services/utils/utils.service';
import {createCroppedImageFile, isImageFile} from '../../core/services/utils/image-upload.utils';

@Component({
  selector: 'app-ecommerce-products',
  templateUrl: './ecommerce-products.component.html',
  styleUrls: ['../ecommerce-shared.css'],
  standalone: false
})
export class EcommerceProductsComponent implements OnInit {
  products: any[] = [];
  categories: any[] = [];
  loading = false;
  saving = false;
  uploading = false;
  showModal = false;
  showThumbnailCropModal = false;
  showGalleryCropModal = false;
  editId = '';
  thumbnailImageFile: File = null;
  croppedThumbnailFile: File = null;
  readonly thumbnailAspectRatio = 1;
  galleryCropQueue: File[] = [];
  galleryCropTotal = 0;
  galleryImageFile: File = null;
  croppedGalleryFile: File = null;
  readonly galleryAspectRatio = 1;
  filters = {keyword: '', category: '', type: '', status: '', currency: ''};
  appliedFilters = {keyword: '', category: '', type: '', status: '', currency: ''};
  form: any = this.emptyForm();

  constructor(private api: ApiService, private utils: UtilsService) {}
  ngOnInit(): void { this.load(); }

  emptyForm(): any {
    return {
      categoryId: '', title: '', slug: '', description: '', thumbnailUrl: '', galleryUrls: [],
      productType: 'physical', price: 0, currency: 'INR', trackInventory: true,
      stockQuantity: 0, shippingCharge: 0, taxRate: 0, weight: null, weightUnit: 'g',
      dimensions: {length: null, width: null, height: null, unit: 'cm'}, downloadFile: null,
      sortOrder: 0, status: 'published', isActive: true
    };
  }

  load(): void {
    this.loading = true;
    this.api.getEcommerceCategories().subscribe({next: (res) => this.categories = res.data || []});
    this.api.getEcommerceProducts().subscribe({
      next: (res) => { this.products = res.data || []; this.loading = false; },
      error: (err) => { this.loading = false; this.utils.resErr(err); }
    });
  }

  get filtered(): any[] {
    const keyword = this.appliedFilters.keyword.toLowerCase().trim();
    return this.products.filter((product) =>
      (!keyword || String(product.title || '').toLowerCase().includes(keyword)) &&
      (!this.appliedFilters.category || String(product.category?._id || product.categoryId) === this.appliedFilters.category) &&
      (!this.appliedFilters.type || product.productType === this.appliedFilters.type) &&
      (!this.appliedFilters.status || product.status === this.appliedFilters.status) &&
      (!this.appliedFilters.currency || product.currency === this.appliedFilters.currency));
  }

  applyFilters(): void {
    this.appliedFilters = {...this.filters, keyword: this.filters.keyword.trim()};
  }

  handleFilterKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') this.applyFilters();
  }

  hasActiveFilters(): boolean {
    return Object.values(this.appliedFilters).some(Boolean);
  }

  clearFilters(): void {
    this.filters = {keyword: '', category: '', type: '', status: '', currency: ''};
    this.appliedFilters = {...this.filters};
  }

  open(item: any = null): void {
    this.editId = item?._id || item?.id || '';
    this.form = item ? {
      ...item,
      categoryId: item.category?._id || item.categoryId,
      price: Number(item.priceMinor || 0) / 100,
      shippingCharge: Number(item.shippingChargeMinor || 0) / 100,
      galleryUrls: [...(item.galleryUrls || [])],
      dimensions: {...(item.dimensions || {})}
    } : this.emptyForm();
    this.showModal = true;
  }

  close(): void {
    this.showModal = false;
    this.resetThumbnailCrop();
    this.resetGalleryCrop();
  }

  upload(event: any, target: 'thumbnail' | 'gallery' | 'download'): void {
    const files = Array.from(event.target.files || []) as File[];
    if (!files.length) return;
    if (target === 'thumbnail') {
      event.target.value = '';
      if (!isImageFile(files[0])) {
        this.utils.toastError('Choose a valid image file.');
        return;
      }
      this.thumbnailImageFile = files[0];
      this.croppedThumbnailFile = null;
      this.showThumbnailCropModal = true;
      return;
    }
    if (target === 'gallery') {
      this.galleryCropQueue = files.filter(isImageFile);
      this.galleryCropTotal = this.galleryCropQueue.length;
      event.target.value = '';
      if (!this.galleryCropTotal) {
        this.utils.toastError('Choose a valid image file.');
        return;
      }
      this.advanceGalleryCrop();
      return;
    }
    this.uploading = true;
    let remaining = files.length;
    files.forEach((file) => this.api.uploadEcommerceAsset(file, target === 'download' ? 'download' : 'image').subscribe({
      next: (res) => {
        if (target === 'download') this.form.downloadFile = res.data;
        else this.form.galleryUrls.push(res.data?.url || res.data?.location);
        if (--remaining === 0) this.uploading = false;
      },
      error: (err) => { this.uploading = false; this.utils.resErr(err); }
    }));
  }

  onThumbnailImageCropped(event: ImageCroppedEvent): void {
    this.croppedThumbnailFile = createCroppedImageFile(event, `product-thumbnail-${Date.now()}.png`);
  }

  onThumbnailImageLoadFailed(): void {
    this.utils.toastError('Image load failed. Please choose another image.');
    this.resetThumbnailCrop();
  }

  uploadCroppedThumbnail(): void {
    if (!this.croppedThumbnailFile) {
      this.utils.toastError('Please adjust the crop area before uploading.');
      return;
    }
    this.uploading = true;
    this.api.uploadEcommerceAsset(this.croppedThumbnailFile, 'image').subscribe({
      next: (res) => {
        this.form.thumbnailUrl = res.data?.url || res.data?.location;
        this.uploading = false;
        this.resetThumbnailCrop();
      },
      error: (err) => { this.uploading = false; this.utils.resErr(err); }
    });
  }

  closeThumbnailCropModal(): void {
    if (this.uploading) return;
    this.resetThumbnailCrop();
  }

  resetThumbnailCrop(): void {
    this.showThumbnailCropModal = false;
    this.thumbnailImageFile = null;
    this.croppedThumbnailFile = null;
  }

  advanceGalleryCrop(): void {
    this.galleryImageFile = this.galleryCropQueue.shift() || null;
    this.croppedGalleryFile = null;
    this.showGalleryCropModal = !!this.galleryImageFile;
    if (!this.showGalleryCropModal) this.galleryCropTotal = 0;
  }

  onGalleryImageCropped(event: ImageCroppedEvent): void {
    this.croppedGalleryFile = createCroppedImageFile(event, `product-gallery-${Date.now()}.png`);
  }

  onGalleryImageLoadFailed(): void {
    this.utils.toastError('Image load failed. Please choose another image.');
    this.advanceGalleryCrop();
  }

  uploadCroppedGallery(): void {
    if (!this.croppedGalleryFile) {
      this.utils.toastError('Please adjust the crop area before uploading.');
      return;
    }
    this.uploading = true;
    this.api.uploadEcommerceAsset(this.croppedGalleryFile, 'image').subscribe({
      next: (res) => {
        this.form.galleryUrls.push(res.data?.url || res.data?.location);
        this.uploading = false;
        this.advanceGalleryCrop();
      },
      error: (err) => { this.uploading = false; this.utils.resErr(err); }
    });
  }

  closeGalleryCropModal(): void {
    if (this.uploading) return;
    this.resetGalleryCrop();
  }

  resetGalleryCrop(): void {
    this.showGalleryCropModal = false;
    this.galleryCropQueue = [];
    this.galleryCropTotal = 0;
    this.galleryImageFile = null;
    this.croppedGalleryFile = null;
  }

  removeGallery(index: number): void { this.form.galleryUrls.splice(index, 1); }

  viewDownloadFile(): void {
    const key = this.form.downloadFile?.key;
    if (!key) return;
    const previewWindow = window.open('', '_blank');
    this.api.previewEcommerceDownload(key).subscribe({
      next: (res) => {
        if (previewWindow && res.data?.url) previewWindow.location.href = res.data.url;
      },
      error: (err) => {
        previewWindow?.close();
        this.utils.resErr(err);
      }
    });
  }

  save(): void {
    if (!this.form.categoryId || !this.form.title || !this.form.description || !this.form.thumbnailUrl) {
      this.utils.toastError('Category, title, description and thumbnail are required.');
      return;
    }
    if (this.form.productType === 'downloadable' && !this.form.downloadFile?.key) {
      this.utils.toastError('Upload the downloadable file.');
      return;
    }
    const payload = {
      ...this.form,
      priceMinor: Math.round(Number(this.form.price || 0) * 100),
      shippingChargeMinor: Math.round(Number(this.form.shippingCharge || 0) * 100)
    };
    this.saving = true;
    const request = this.editId
      ? this.api.updateEcommerceProduct(this.editId, payload)
      : this.api.createEcommerceProduct(payload);
    request.subscribe({
      next: (res) => { this.saving = false; this.close(); this.utils.toastSuccess(res.message); this.load(); },
      error: (err) => { this.saving = false; this.utils.resErr(err); }
    });
  }

  remove(item: any): void {
    if (!confirm(`Delete ${item.title}? Products with orders must be archived instead.`)) return;
    this.api.deleteEcommerceProduct(item._id || item.id).subscribe({
      next: (res) => { this.utils.toastSuccess(res.message); this.load(); },
      error: (err) => this.utils.resErr(err)
    });
  }

  money(minor: number, currency: string): string {
    return new Intl.NumberFormat('en-IN', {style: 'currency', currency}).format(Number(minor || 0) / 100);
  }
}
